import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Components imports
import Login from '../components/Auth/Login';
import PasswordReset from '../components/Auth/PasswordReset';
import SuperAdminSetup from '../components/Setup/SuperAdminSetup';
import InitialSetupGuard from './InitialSetupGuard';
import PrivateRoute from './PrivateRoute';
import Layout from '../components/Layout/Layout';

// Super Admin Components
import SuperAdminDashboard from '../components/SuperAdmin/Dashboard';
import EstablishmentManagement from '../components/SuperAdmin/EstablishmentManagement';
import AdminManagement from '../components/SuperAdmin/AdminManagement';
import Statistics from '../components/SuperAdmin/Statistics';
import Settings from '../components/SuperAdmin/Settings';

// Admin Components
import Dashboard from '../components/Admin/Dashboard';
import PersonnelManagement from '../components/Admin/PersonnelManagement';
import PersonnelDetails from '../components/Admin/PersonnelDetails';
import DepartmentManagement from '../components/Admin/DepartmentManagement';
import MessageCenter from '../components/Admin/MessageCenter';
import ScheduleManager from '../components/Admin/ScheduleManager';
import RequestsManagement from '../components/Admin/RequestsManagement';
import NotificationsCenter from '../components/Admin/NotificationsCenter';
import SchoolCalendar from '../components/Admin/SchoolCalendar';
import AdminAbsenceCalendar from '../components/Admin/AbsenceCalendar';

// Chef de Service Components
import ChefServiceDashboard from '../components/ChefService/Dashboard';
import TimeTableView from '../components/ChefService/TimeTableView';
import LeaveRequests from '../components/ChefService/LeaveRequests';
import PersonnelManagementChef from '../components/ChefService/PersonnelManagement';
import ChefServiceCalendar from '../components/ChefService/SchoolCalendar';
import ChefServiceMessages from '../components/ChefService/Messages';
import AbsenceDashboard from '../components/ChefService/AbsenceDashboard';
import AbsenceCalendar from '../components/ChefService/AbsenceCalendar';
import ChefServiceScheduleManager from '../components/ChefService/ScheduleManager';
import OvertimeManagement from '../components/ChefService/OvertimeManagement';
import MyAbsenceRequests from '../components/ChefService/MyAbsenceRequests';

// Personnel Components
import PersonnelDashboard from '../components/Personnel/Dashboard';
import Calendar from '../components/Personnel/Calendar';
import LeaveRequest from '../components/Personnel/LeaveRequest';
import PersonnelMessages from '../components/Personnel/Messages';
import Profile from '../components/Personnel/Profile';
import PersonnelTimeTable from '../components/Personnel/TimeTable';
import Permanences from '../components/Personnel/Permanences';

const AppRoutes = () => {
  const { currentUser, userRole } = useAuth();

  // Redirection based on user role
  const getDefaultRoute = () => {
    switch (userRole) {
      case 'superAdmin':
        return '/super-admin/dashboard';
      case 'admin':
        return '/admin/dashboard';
      case 'directionRH': // Direction RH is redirected to admin dashboard
        return '/admin/dashboard';
      case 'chefService':
        return '/chef-service/dashboard';
      case 'personnel':
        return '/personnel/dashboard';
      default:
        return '/login';
    }
  };

  const wrapInLayout = (component) => (
    <Layout>{component}</Layout>
  );

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/setup" element={<InitialSetupGuard><SuperAdminSetup /></InitialSetupGuard>} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<PasswordReset />} />

      {/* Super Admin Routes */}
      <Route path="/super-admin">
        <Route path="dashboard" element={<PrivateRoute allowedRoles={['superAdmin']}>{wrapInLayout(<SuperAdminDashboard />)}</PrivateRoute>} />
        <Route path="establishments" element={<PrivateRoute allowedRoles={['superAdmin']}>{wrapInLayout(<EstablishmentManagement />)}</PrivateRoute>} />
        <Route path="admins" element={<PrivateRoute allowedRoles={['superAdmin']}>{wrapInLayout(<AdminManagement />)}</PrivateRoute>} />
        <Route path="statistics" element={<PrivateRoute allowedRoles={['superAdmin']}>{wrapInLayout(<Statistics />)}</PrivateRoute>} />
        <Route path="settings" element={<PrivateRoute allowedRoles={['superAdmin']}>{wrapInLayout(<Settings />)}</PrivateRoute>} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin">
        <Route index element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<Dashboard />)}</PrivateRoute>} />
        <Route path="dashboard" element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<Dashboard />)}</PrivateRoute>} />
        <Route path="personnel" element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<PersonnelManagement />)}</PrivateRoute>} />
        <Route path="personnel/:id" element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<PersonnelDetails />)}</PrivateRoute>} />
        <Route path="departments" element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<DepartmentManagement />)}</PrivateRoute>} />
        <Route path="messages" element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<MessageCenter />)}</PrivateRoute>} />
        <Route path="schedule" element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<ScheduleManager />)}</PrivateRoute>} />
        <Route path="requests" element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<RequestsManagement />)}</PrivateRoute>} />
        <Route path="notifications" element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<NotificationsCenter />)}</PrivateRoute>} />
        <Route path="calendar" element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<SchoolCalendar />)}</PrivateRoute>} />
        <Route path="permanences" element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<ScheduleManager />)}</PrivateRoute>} />
        <Route path="absences/calendar" element={<PrivateRoute allowedRoles={['admin', 'directionRH']}>{wrapInLayout(<AdminAbsenceCalendar />)}</PrivateRoute>} />
      </Route>

      {/* Chef de Service Routes */}
      <Route path="/chef-service">
        <Route path="dashboard" element={<PrivateRoute allowedRoles={['chefService']}>{wrapInLayout(<ChefServiceDashboard />)}</PrivateRoute>} />
        <Route path="personnel" element={<PrivateRoute allowedRoles={['chefService']}>{wrapInLayout(<PersonnelManagementChef />)}</PrivateRoute>} />
        <Route path="timetable" element={<PrivateRoute allowedRoles={['chefService']}>{wrapInLayout(<TimeTableView />)}</PrivateRoute>} />
        <Route path="leave-requests" element={<PrivateRoute allowedRoles={['chefService']}>{wrapInLayout(<LeaveRequests />)}</PrivateRoute>} />
        <Route path="absences" element={<PrivateRoute allowedRoles={['chefService']}>{wrapInLayout(<AbsenceDashboard />)}</PrivateRoute>} />
        <Route path="absences/calendar" element={<PrivateRoute allowedRoles={['chefService']}>{wrapInLayout(<AbsenceCalendar />)}</PrivateRoute>} />
        <Route path="my-absences" element={<PrivateRoute allowedRoles={['chefService']}>{wrapInLayout(<MyAbsenceRequests />)}</PrivateRoute>} />
        <Route path="calendar" element={<PrivateRoute allowedRoles={['chefService']}>{wrapInLayout(<ChefServiceCalendar />)}</PrivateRoute>} />
        <Route path="messages" element={<PrivateRoute allowedRoles={['chefService']}>{wrapInLayout(<ChefServiceMessages />)}</PrivateRoute>} />
        <Route path="permanences" element={<PrivateRoute allowedRoles={['chefService']}>{wrapInLayout(<ChefServiceScheduleManager />)}</PrivateRoute>} />
        <Route path="overtimes" element={<PrivateRoute allowedRoles={['chefService']}>{wrapInLayout(<OvertimeManagement />)}</PrivateRoute>} />
      </Route>

      {/* Personnel Routes */}
      <Route path="/personnel">
        <Route path="dashboard" element={<PrivateRoute allowedRoles={['personnel']}>{wrapInLayout(<PersonnelDashboard />)}</PrivateRoute>} />
        <Route path="timetable" element={<PrivateRoute allowedRoles={['personnel']}>{wrapInLayout(<PersonnelTimeTable />)}</PrivateRoute>} />
        <Route path="calendar" element={<PrivateRoute allowedRoles={['personnel']}>{wrapInLayout(<Calendar />)}</PrivateRoute>} />
        <Route path="leave-request" element={<PrivateRoute allowedRoles={['personnel']}>{wrapInLayout(<LeaveRequest />)}</PrivateRoute>} />
        <Route path="permanences" element={<PrivateRoute allowedRoles={['personnel']}>{wrapInLayout(<Permanences />)}</PrivateRoute>} />
        <Route path="messages" element={<PrivateRoute allowedRoles={['personnel']}>{wrapInLayout(<PersonnelMessages />)}</PrivateRoute>} />
        <Route path="profile" element={<PrivateRoute allowedRoles={['personnel']}>{wrapInLayout(<Profile />)}</PrivateRoute>} />
      </Route>

      {/* Default Route */}
      <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
      <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
    </Routes>
  );
};

export default AppRoutes;
